import './Header.scss';

import { FC, useContext, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { AppContext } from 'components/AppContext/AppContext';
import { Container, Dropdown, Nav, Navbar, NavItem } from 'react-bootstrap';
import Image from 'react-bootstrap/Image';
import { Link, NavLink, useLocation, useSearchParams } from 'react-router-dom';

import { ModuleDeclaration } from '../../modules/ModuleDeclaration';
import SelectProject from '../SelectProject/SelectProject';

interface HeaderProps {
  routeProps: Array<ModuleDeclaration>;
}

const Header: FC<HeaderProps> = ({ routeProps }) => {
  const location = useLocation();
  const { logout, isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const { configRepo } = useContext(AppContext);
  const unauthenticatedRoutes = ['register'];
  const [searchParams] = useSearchParams();
  const hideNav = searchParams.get('nav_hidden');
  const isUnauthenticatedPath =
    hideNav || unauthenticatedRoutes.some((r) => location.pathname.includes(r));

  const [isAccountLinkVisible, setIsAccountLinkVisible] = useState(false);

  useEffect(() => {
    const accessibleCompanyIds = 'https://api.blabberjax.com/accessibleCompanyIds';

    const parseJwt = (token: string) => {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        window
          .atob(base64)
          .split('')
          .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
          .join(''),
      );
      return JSON.parse(jsonPayload);
    };

    const fetchData = async () => {
      if (isLoading || !isAuthenticated) {
        return;
      }

      const rawToken = await getAccessTokenSilently();
      const token = parseJwt(rawToken);
      if (token[accessibleCompanyIds] && token[accessibleCompanyIds].length > 0) {
        setIsAccountLinkVisible(true);
      } else {
        setIsAccountLinkVisible(false);
      }
    };

    fetchData();
  }, [isLoading, isAuthenticated]);

  const endSessionClick = () => {
    logout({ logoutParams: { returnTo: configRepo!.APP_URL } });
  };

  const onCreateNewProject = () => {
    window.location.href = `/account?new_project=true`;
  };

  if (isUnauthenticatedPath) {
    return (
      <div className="Header">
        <Navbar expand="lg" className="main-nav">
          <Container fluid style={{ marginLeft: '2.5em' }}>
            <Navbar.Brand href="/">
              <img src="/img/logo.png" alt="BlabberJax Logo" className="logo" />
            </Navbar.Brand>
          </Container>
        </Navbar>
      </div>
    );
  }

  const getPosition = (string: string, subString: string, index: number) => string.split(subString, index).join(subString).length;
  const getMainRoute = (pathName: string) => {
    const index = getPosition(pathName, '/', 2);
    const activeKey = pathName.substring(0, index);
    return activeKey.toLowerCase();
  };

  return (
    <div className="Header">
      <Navbar expand="lg" className="main-nav">
        <Container fluid style={{ marginLeft: '2.5em' }}>
          <Navbar.Brand href="/">
            <img src="/img/logo.png" alt="BlabberJax Logo" className="logo" />
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="basic-navbar-nav" />
          <Navbar.Collapse id="basic-navbar-nav">
            <Nav activeKey={getMainRoute(location.pathname)} className="app-nav">
              {routeProps.map((route) => (
                <Nav.Link href={route.routeProps.path} key={route.name}>
                  <i className={route.iconClassName} />
                  <span className="route-name">{route.name}</span>
                </Nav.Link>
              ))}
            </Nav>
          </Navbar.Collapse>
          <Navbar.Collapse className="justify-content-end">
            <SelectProject />
            <Dropdown as={NavItem} className="settings-dropdown">
              <Dropdown.Toggle className="toggle px-0">
                <i className="bi bi-gear" />
                <span className="mobile-text">Settings</span>
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {isAccountLinkVisible ? (
                  <Dropdown.Item
                    className="project-settings"
                    as={NavLink}
                    to="/account"
                    reloadDocument
                  >
                    Project Settings
                  </Dropdown.Item>
                ) : null}
                <Dropdown.Item className="project-settings" onClick={onCreateNewProject}>
                  New Project <i className="bi bi-plus" />
                </Dropdown.Item>
                <Dropdown.Item className="billing" href='https://billing.stripe.com/p/login/6oEbLl3Fl6EN1QA288' target='_blank'>
                  Billing
                </Dropdown.Item>
                <Dropdown.Item className="logout" onClick={endSessionClick}>
                  Logout <i className="bi  bi-box-arrow-right" />
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
            <div className='app-nav navbar-nav'>
              <Link to='/welcome' className='nav-link'>
                <Image
                  width={16}
                  height={22}
                  src="/whizzandra-icon-v3.png"
                  alt='Help'
                  title='Get Help' />
                <span className="mobile-text help-action">&nbsp;Get Help</span>
              </Link>
            </div>
          </Navbar.Collapse>
        </Container>
      </Navbar>
    </div>
  );
};

export default Header;
